import React, { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { UploadCloud, FileBox, X, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const ACCEPT = ".stl,.step,.stp,.obj,.3mf,.zip";
const ALLOWED = ["stl", "step", "stp", "obj", "3mf", "zip"];
const MAX = 60 * 1024 * 1024;

const TECHS = ["FDM", "SLA Resina", "SLS Nylon", "Não sei / aconselhem"];
const MATERIALS = ["PLA", "PETG", "PETG-CF (fibra carbono)", "ABS", "TPU (flexível)", "Resina Tough", "Nylon SLS", "Não sei / aconselhem"];
const PROJECTS = ["Prototipagem", "Peça final / produção", "Reposição / manutenção", "Investigação / laboratório", "Outro"];

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "", company: "", email: "", whatsapp: "",
    project_type: "", technology: "", material: "", quantity: 1,
    deadline: "", description: "", nda_requested: false,
  });
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = useCallback((list) => {
    const incoming = Array.from(list);
    const valid = [];
    for (const f of incoming) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`Formato não suportado: ${f.name}`); continue; }
      if (f.size > MAX) { toast.error(`Ficheiro demasiado grande: ${f.name} (máx 60 MB)`); continue; }
      valid.push(f);
    }
    if (valid.length) setFiles((prev) => [...prev, ...valid]);
  }, []);

  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files) addFiles(e.dataTransfer.files); };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("Preencha nome, email e descrição.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/quotes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data);
      toast.success(`Pedido enviado! Referência ${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="max-w-2xl mx-auto text-center bg-ax-card border border-ax-border rounded-2xl p-10">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-5" />
        <h3 className="font-heading text-2xl font-bold text-ax-text">Pedido recebido!</h3>
        <p className="text-ax-sub mt-3">
          A sua referência é <span className="font-mono text-ax-orange font-bold">{done.ref_code}</span>.
          Enviámos um email de confirmação e vamos responder normalmente em menos de 2 horas úteis.
        </p>
        <button
          data-testid="quote-new"
          onClick={() => { setDone(null); setFiles([]); setForm({ name: "", company: "", email: "", whatsapp: "", project_type: "", technology: "", material: "", quantity: 1, deadline: "", description: "", nda_requested: false }); }}
          className="mt-6 text-ax-cyan hover:text-ax-cyanhover font-medium"
        >
          Enviar outro pedido
        </button>
      </div>
    );
  }

  return (
    <form data-testid="quote-form-container" onSubmit={submit} className="max-w-4xl mx-auto bg-ax-card border border-ax-border rounded-2xl p-6 sm:p-9 shadow-2xl shadow-black/40">
      <div
        data-testid="quote-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed transition-all p-8 text-center ${
          drag ? "border-ax-orange bg-ax-orange/5" : "border-ax-border hover:border-ax-cyan bg-ax-surface/50"
        }`}
      >
        <input ref={inputRef} data-testid="quote-file-input" type="file" multiple accept={ACCEPT} className="hidden"
          onChange={(e) => addFiles(e.target.files)} />
        <UploadCloud className="w-10 h-10 mx-auto text-ax-cyan mb-3" />
        <p className="font-heading font-semibold text-ax-text">Arraste os seus ficheiros 3D ou clique para carregar</p>
        <p className="text-sm text-ax-muted mt-1 font-mono">STL · STEP · OBJ · 3MF · ZIP — até 60 MB cada</p>
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="quote-file-list">
          {files.map((f, i) => (
            <div key={`${f.name}-${f.size}`} className="flex items-center justify-between bg-ax-surface border border-ax-border rounded-lg px-4 py-2.5">
              <div className="flex items-center gap-3 min-w-0">
                <FileBox className="w-5 h-5 text-ax-orange shrink-0" />
                <span className="truncate text-sm text-ax-text">{f.name}</span>
                <span className="text-xs text-ax-muted font-mono shrink-0">{fmtSize(f.size)}</span>
              </div>
              <button type="button" data-testid={`quote-file-remove-${i}`} onClick={() => removeFile(i)} className="text-ax-muted hover:text-red-400">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-7">
        <Field label="Nome *"><Input data-testid="quote-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="O seu nome" className="ax-input" /></Field>
        <Field label="Empresa"><Input data-testid="quote-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Opcional" className="ax-input" /></Field>
        <Field label="Email *"><Input data-testid="quote-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@empresa.pt" className="ax-input" /></Field>
        <Field label="WhatsApp"><Input data-testid="quote-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+351 9XX XXX XXX" className="ax-input" /></Field>

        <Field label="Tipo de projeto">
          <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
            <SelectTrigger data-testid="quote-project" className="ax-input"><SelectValue placeholder="Selecionar" /></SelectTrigger>
            <SelectContent>{PROJECTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Tecnologia">
          <Select value={form.technology} onValueChange={(v) => set("technology", v)}>
            <SelectTrigger data-testid="quote-tech" className="ax-input"><SelectValue placeholder="Selecionar" /></SelectTrigger>
            <SelectContent>{TECHS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Material">
          <Select value={form.material} onValueChange={(v) => set("material", v)}>
            <SelectTrigger data-testid="quote-material" className="ax-input"><SelectValue placeholder="Selecionar" /></SelectTrigger>
            <SelectContent>{MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Quantidade"><Input data-testid="quote-qty" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", Math.max(1, parseInt(e.target.value || "1")))} className="ax-input" /></Field>
          <Field label="Prazo"><Input data-testid="quote-deadline" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} placeholder="ex: 1 semana" className="ax-input" /></Field>
        </div>
      </div>

      <div className="mt-4">
        <Field label="Descrição da necessidade *">
          <Textarea data-testid="quote-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4}
            placeholder="Descreva a peça, aplicação, requisitos técnicos, tolerâncias..." className="ax-input resize-none" />
        </Field>
      </div>

      <label className="flex items-center gap-3 mt-5 cursor-pointer select-none">
        <Checkbox data-testid="quote-nda" checked={form.nda_requested} onCheckedChange={(v) => set("nda_requested", !!v)} />
        <span className="text-sm text-ax-sub flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-ax-cyan" /> Pretendo um Acordo de Confidencialidade (NDA) antes de partilhar os modelos.
        </span>
      </label>

      <button
        type="submit"
        data-testid="quote-submit"
        disabled={loading}
        className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-ax-orange hover:bg-ax-orangehover disabled:opacity-60 text-white font-semibold px-6 py-4 rounded-lg transition-all hover:ax-glow"
      >
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> A enviar...</> : <><UploadCloud className="w-5 h-5" /> Enviar pedido de orçamento</>}
      </button>
      <p className="text-center text-xs text-ax-muted mt-3">Avaliação caso a caso · sem compromisso · resposta rápida</p>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-ax-sub mb-1.5 block">{label}</Label>
    {children}
  </div>
);
